import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { 
  getRouteProposals as getRouteProposalsApi,
  getRouteProposal as getRouteProposalApi,
  addRouteProposal as addRouteProposalApi,
  updateRouteProposal as updateRouteProposalApi,
  deleteRouteProposal as deleteRouteProposalApi,
  submitRouteProposal as submitRouteProposalApi,
  reviewRouteProposal as reviewRouteProposalApi,
  approveRouteProposal as approveRouteProposalApi,
  rejectRouteProposal as rejectRouteProposalApi
} from '@services/passenger-bus'

// Async actions
export const fetchRouteProposals = createAsyncThunk(
  'routeProposals/fetchRouteProposals',
  async (params) => {
    const response = await getRouteProposalsApi(params)
    return response
  }
)

export const fetchRouteProposal = createAsyncThunk(
  'routeProposals/fetchRouteProposal',
  async (id) => {
    const response = await getRouteProposalApi(id)
    return response
  }
)

export const addRouteProposal = createAsyncThunk(
  'routeProposals/addRouteProposal',
  async (payload) => {
    const response = await addRouteProposalApi(payload)
    return response
  }
)

export const updateRouteProposal = createAsyncThunk(
  'routeProposals/updateRouteProposal',
  async (payload) => {
    const response = await updateRouteProposalApi(payload)
    return response
  }
)

export const submitRouteProposal = createAsyncThunk(
  'routeProposals/submitRouteProposal',
  async (id) => {
    const response = await submitRouteProposalApi(id)
    return response
  }
)

export const reviewRouteProposal = createAsyncThunk(
  'routeProposals/reviewRouteProposal',
  async ({ id, decision, remarks }) => {
    const response = await reviewRouteProposalApi(id, { decision, remarks })
    return response
  }
)

export const approveRouteProposal = createAsyncThunk(
  'routeProposals/approveRouteProposal',
  async ({ id, remarks }) => {
    const response = await approveRouteProposalApi(id, { remarks })
    return response
  }
)

export const rejectRouteProposal = createAsyncThunk(
  'routeProposals/rejectRouteProposal',
  async ({ id, remarks }) => {
    const response = await rejectRouteProposalApi(id, { remarks })
    return response
  }
)

export const deleteRouteProposal = createAsyncThunk(
  'routeProposals/deleteRouteProposal',
  async (id) => {
    await deleteRouteProposalApi(id)
    return id
  }
)

// Slice
const routeProposalsSlice = createSlice({
  name: 'routeProposals',
  initialState: {
    data: [],
    meta: { currentPage: 1, itemCount: 0, itemsPerPage: 10, totalItems: 0, totalPages: 1 },
    params: { page: 1, take: 10, q: '', status: '' },
    selected: null,
    loading: false,
    error: null
  },
  reducers: {
    setRouteProposalParams: (state, action) => {
      state.params = { ...state.params, ...action.payload }
    },
    clearSelectedRouteProposal: (state) => {
      state.selected = null
    },
    clearRouteProposalError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch route proposals
      .addCase(fetchRouteProposals.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRouteProposals.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload.data
        state.meta = action.payload.meta
        state.params = action.payload.params
      })
      .addCase(fetchRouteProposals.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
      
      // Fetch single route proposal
      .addCase(fetchRouteProposal.fulfilled, (state, action) => {
        state.selected = action.payload
      })
      
      // Add route proposal
      .addCase(addRouteProposal.fulfilled, (state, action) => {
        state.data.unshift(action.payload)
      })
      
      // Update route proposal
      .addCase(updateRouteProposal.fulfilled, (state, action) => {
        const index = state.data.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.data[index] = action.payload
        }
        if (state.selected?.id === action.payload.id) {
          state.selected = action.payload
        }
      })
      
      // Submit route proposal
      .addCase(submitRouteProposal.fulfilled, (state, action) => {
        const index = state.data.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.data[index] = action.payload
        }
        if (state.selected?.id === action.payload.id) {
          state.selected = action.payload
        }
      })
      
      // Review route proposal
      .addCase(reviewRouteProposal.fulfilled, (state, action) => {
        const index = state.data.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.data[index] = action.payload
        }
        if (state.selected?.id === action.payload.id) {
          state.selected = action.payload
        }
      })
      
      // Approve route proposal
      .addCase(approveRouteProposal.fulfilled, (state, action) => {
        const index = state.data.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.data[index] = action.payload
        }
        if (state.selected?.id === action.payload.id) {
          state.selected = action.payload
        }
      })
      
      // Reject route proposal
      .addCase(rejectRouteProposal.fulfilled, (state, action) => {
        const index = state.data.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.data[index] = action.payload
        }
        if (state.selected?.id === action.payload.id) {
          state.selected = action.payload
        }
      })
      
      // Delete route proposal
      .addCase(deleteRouteProposal.fulfilled, (state, action) => {
        state.data = state.data.filter(item => item.id !== action.payload)
        if (state.selected?.id === action.payload) {
          state.selected = null
        }
      })
  }
})

export const { setRouteProposalParams, clearSelectedRouteProposal, clearRouteProposalError } = routeProposalsSlice.actions
export default routeProposalsSlice.reducer
